import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const apiUrl = process.env.REACT_APP_API_URL || "";

/**
 * Hook for searching users by username or name
 *
 * @param {string} query - Search query string
 * @param {object} options - Additional options
 * @param {number} options.page - Page number for pagination (default: 1)
 * @param {number} options.limit - Results per page (default: 10)
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @returns {object} React Query result with users array and pagination info
 */
export function useUserSearch(query, options = {}) {
  const { getAccessTokenSilently } = useAuth0();
  const { page = 1, limit = 10, enabled } = options;

  // Only search if query is at least 2 characters
  // Ensure enabled is explicitly a boolean
  const isEnabled = enabled !== undefined ? Boolean(enabled) : true;
  const shouldSearch = isEnabled && Boolean(query && query.trim().length >= 2);

  return useQuery(
    ["userSearch", query, page, limit],
    async () => {
      const token = await getAccessTokenSilently();
      const trimmedQuery = query.trim();

      const { data } = await axios.get(`${apiUrl}/api/users/search`, {
        params: {
          q: trimmedQuery,
          page,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return data;
    },
    {
      enabled: shouldSearch,
      staleTime: 30 * 1000, // 30 seconds
      cacheTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true, // Keep previous results while loading new ones
      retry: 1,
    },
  );
}
